import { Node as ReactFlowNode } from '@xyflow/react'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { filter, forEach, keyBy, map, set } from 'lodash'

import { Node } from '@/api/Project/types'
import { TaskType } from '@/api/Task/types'

import { FocusedNode, INITIAL_STATE, PipelineState } from '@/stores/slices/RenewedPipeline/const'
import { GenericNode, NodeTypes } from '@/pages/RenewedPipeline/Nodes/types'
import { transformNodes } from '@/pages/RenewedPipeline/Nodes/utils'
import { TaskStatusValueType } from '@/pages/const'

export const pipelineSlice = createSlice({
  name: 'renewedPipeline',
  initialState: INITIAL_STATE,
  reducers: {
    resetPipelineState: (state, action: PayloadAction<{ preserveFocus?: boolean } | undefined>) => {
      const preserveFocus = action.payload?.preserveFocus ?? false

      return {
        ...INITIAL_STATE,
        isOpsView: state.isOpsView, // 의도하지 않은 pipeline 변경 방지
        projectReferenceTime: state.projectReferenceTime,
        focusedNode: preserveFocus && state.focusedNode ? { ...state.focusedNode, openDrawer: false } : undefined,
        zoomLevel: preserveFocus ? state.zoomLevel : 1,
      }
    },
    addPlaceholderNode: (state, action: PayloadAction<PipelineState['placeholder']>) => {
      state.placeholder = action.payload || { position: { x: 0, y: 0 } }
    },
    updatePlaceholderPosition: (state, action: PayloadAction<{ x: number, y: number }>) => {
      state.placeholder = { ...state.placeholder, position: action.payload }
    },
    // placeholder를 통해 새로운 노드 생성 후 삭제인 경우 생성된 노드를 타입과 함께 payload에 첨부
    removePlaceholderNode: (state, action: PayloadAction<{ type: NodeTypes, nodeInfo: GenericNode } | undefined>) => {
      if (action.payload) {
        const { type, nodeInfo } = action.payload

        state.nodes = [...state.nodes, ...transformNodes(type, [nodeInfo])]
        state.focusedNode = {
          id: nodeInfo._id,
          nodeType: type,
          position: nodeInfo.metadata.position,
          isActive: nodeInfo.isActive ?? false,
          openDrawer: false,
        }
      }
      state.placeholder = undefined
    },
    updateProjectReferenceTime: (state, action: PayloadAction<any>) => {
      state.projectReferenceTime = action.payload
    },
    updateNodes: (state, action: PayloadAction<{ nodes: ReactFlowNode[], overwrite?: boolean }>) => {
      const { nodes, overwrite } = action.payload
      if (overwrite) {
        state.nodes = nodes
      } else {
        const previousNodesById = keyBy(state.nodes, 'id')
        forEach(nodes, (n) => set(previousNodesById, n.id, n))
        state.nodes = map(previousNodesById, (val, key) => val)
      }
    },
    removeNodes: (state, action: PayloadAction<string[]>) => {
      state.nodes = filter(state.nodes, (n) => !action.payload.includes(n.id))
    },
    // 노드 내의 data 객체만 업데이트 (노드 타입과 상관 없이 노드 정보 get 해온 응답값 통채로 넘겨주면 됨)
    updateNodeData: (state, action: PayloadAction<{ node: GenericNode, overwrite?: boolean }>) => {
      const { node, overwrite = false } = action.payload

      state.nodes = map(state.nodes, (n) => {
        if (n.id === node._id) {
          // return overwrite
          //   ? { ...n, position: get(node, 'metadata.position', {}), data: node }
          //   : { ...n, position: get(node, 'metadata.position', {}), data: { ...n.data, ...node } }
          return overwrite
            ? { ...n, data: { ...node } }
            : { ...n, data: { ...n.data, ...node } }
        }
        return n
      })
    },
    updateNodePosition: (state, action: PayloadAction<Node>) => {
      const { nodeId, position } = action.payload
      state.nodes = map(state.nodes, (n) => {
        if (n.id === nodeId) {
          return { ...n, position }
        }
        return n
      })
    },
    copyNode: (state, action: PayloadAction<{ id: string, nodeType: NodeTypes, isCopyAll: boolean }>) => {
      if (state.copiedModelVersion) {
        state.copiedModelVersion = undefined
      }
      state.copiedNode = action.payload
    },
    removeCopiedNode: (state, action: PayloadAction<void>) => {
      state.copiedNode = undefined
    },
    copyModelVersion: (state, action: PayloadAction<{ modelVersionId: string }>) => {
      if (state.copiedNode) {
        state.copiedNode = undefined
      }
      state.copiedModelVersion = action.payload
    },
    updateCopiedModelVersionPosition: (state, action: PayloadAction<{ position?: {x: number, y: number}}>) => {
      const { position } = action.payload
      if (state.copiedModelVersion) {
        state.copiedModelVersion = { ...state.copiedModelVersion, position }
      }
    },
    removeCopiedModelVersion: (state, action: PayloadAction<void>) => {
      state.copiedModelVersion = undefined
    },
    setIsDataDriftNodeCreated: (state, action: PayloadAction<boolean>) => {
      state.isDataDriftNodeCreated = action.payload
    },
    setPipelineWarningDialog: (state, action: PayloadAction<{ title: string, message: string, isOpen: boolean }>) => {
      state.pipelineWarningDialog = action.payload
    },
    /* TASKS */
    setRunningTaskId: (state, action: PayloadAction<string | undefined>) => {
      state.runningTask.taskId = action.payload
    },
    setRunningTaskRefetch: (state, action: PayloadAction<boolean>) => {
      state.runningTask.refetch = action.payload
    },
    setRunningTaskTemporaryDisableFlag: (state, action: PayloadAction<boolean>) => {
      state.runningTask.temporaryDisableFlag = action.payload
    },
    updateNodeTaskStatus: (state, action: PayloadAction<{ nodeId: string, status: TaskStatusValueType, taskType: TaskType['type'] }>) => {
      const { nodeId, status, taskType } = action.payload
      if (taskType === 'SEMI_AUTO_LABELING') {
        state.nodes = map(state.nodes, (n) => (n.id === nodeId ? { ...n, data: { ...n.data, jobStatus: status } } : n))
      } else {
        state.nodes = map(state.nodes, (n) => (n.id === nodeId ? { ...n, data: { ...n.data, status } } : n))
      }
    },
    setOpenSidePanel: (state, action: PayloadAction<boolean>) => {
      if (state.focusedNode?.openDrawer) {
        state.focusedNode.openDrawer = false
      }
      state.openSidePanel = action.payload
    },
    setOpenSettings: (state, action: PayloadAction<boolean>) => {
      state.openSettings = action.payload
    },
    setFocusedNode: (state, action: PayloadAction<FocusedNode | undefined>) => {
      state.focusedNode = action.payload
    },
    setOpenDrawer: (state, action: PayloadAction<boolean>) => {
      if (state.openSidePanel) {
        state.openSidePanel = false
      }
      if (state.focusedNode) {
        state.focusedNode = { ...state.focusedNode, openDrawer: action.payload }
      }
    },
    setZoomLevel: (state, action: PayloadAction<number>) => {
      state.zoomLevel = action.payload
    },
    setHasUserUploadedThumbnail: (state, action: PayloadAction<boolean>) => {
      state.hasUserUploadedThumbnail = action.payload
    },
    setOpsView: (state, action: PayloadAction<boolean>) => {
      state.isOpsView = action.payload
    },
  },
})

export const {
  resetPipelineState,
  addPlaceholderNode,
  updatePlaceholderPosition,
  removePlaceholderNode,
  updateProjectReferenceTime,
  updateNodes,
  removeNodes,
  updateNodeData,
  updateNodePosition,
  copyNode,
  removeCopiedNode,
  copyModelVersion,
  updateCopiedModelVersionPosition,
  removeCopiedModelVersion,
  setIsDataDriftNodeCreated,
  setPipelineWarningDialog,
  setHasUserUploadedThumbnail,

  setRunningTaskId,
  setRunningTaskRefetch,
  setRunningTaskTemporaryDisableFlag,
  updateNodeTaskStatus,
  setOpenSidePanel,
  setOpenSettings,
  setFocusedNode,
  setOpenDrawer,
  setZoomLevel,

  setOpsView,
} = pipelineSlice.actions

const pipelineReducers = pipelineSlice.reducer

export default pipelineReducers
